import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { MessageCircle, Plus, ChevronDown, ChevronUp, Users } from 'lucide-react';
import { useChatStore } from '@auw/stores';
import { formatRelativeTime as formatRelative } from '@auw/i18n';
import i18n from '@auw/i18n';
import type { Conversation } from '@auw/workspace-utils';

interface AgentWidgetListProps {
  onSelectConversation?: (conversationId: string) => void;
  onCreateConversation?: () => void;
}

function getLastMessagePreview(conversation: Conversation): string {
  for (let i = conversation.messages.length - 1; i >= 0; i -= 1) {
    const message = conversation.messages[i];
    if (message.role !== 'user' && message.role !== 'assistant') continue;
    const trimmed = message.content.trim();
    if (trimmed.length > 0) return trimmed;
  }
  return i18n.t('common:chat.startConversation');
}

function formatRelTime(updatedAt: number): string {
  return formatRelative(updatedAt);
}

function isTeamGroupConversation(conversation: Conversation): boolean {
  return (
    conversation.workspaceScope === 'team'
    && !!conversation.sharedGroup
    && conversation.sharedGroup.state !== 'team_deleted'
  );
}

export const AgentWidgetList: React.FC<AgentWidgetListProps> = ({
  onSelectConversation,
  onCreateConversation,
}) => {
  const { t } = useTranslation('widget');
  const conversations = useChatStore((s) => s.conversations);
  const conversationOrder = useChatStore((s) => s.conversationOrder);
  const workspaceOrder = useChatStore((s) => s.workspaceOrder);
  const agentTotalItemCount = useChatStore((s) => s.totalItemCountByMode.agent);
  const loadingMoreItems = useChatStore((s) => s.loadingMoreItems);
  const loadMoreItems = useChatStore((s) => s.loadMoreItems);
  const activeId = useChatStore((s) => s.activeId);
  const setActiveConversation = useChatStore((s) => s.setActiveConversation);
  const agentPictures: Record<string, string | null> = {};
  const [personalOpen, setPersonalOpen] = useState(true);
  const [teamGroupOpen, setTeamGroupOpen] = useState(true);
  const [agentAvatarError, setAgentAvatarError] = useState(false);

  const agentAvatar = agentPictures.agent ?? null;

  useEffect(() => {
    setAgentAvatarError(false);
  }, [agentAvatar]);

  const agentConversations = useMemo(
    () =>
      conversationOrder
        .map((id) => conversations[id])
        .filter((conversation): conversation is Conversation =>
          !!conversation
          && conversation.mode === 'agent'
          && conversation.workspaceKind === 'page'),
    [conversationOrder, conversations],
  );

  const teamGroupConversations = useMemo(
    () => agentConversations.filter(isTeamGroupConversation),
    [agentConversations],
  );

  const personalConversations = useMemo(
    () => agentConversations.filter((conversation) => !isTeamGroupConversation(conversation)),
    [agentConversations],
  );

  const loadedAgentWorkspaceCount = useMemo(
    () => workspaceOrder.filter((id) => conversations[id]?.mode === 'agent').length,
    [conversations, workspaceOrder],
  );
  const canLoadMore = typeof agentTotalItemCount === 'number' && loadedAgentWorkspaceCount < agentTotalItemCount;

  useEffect(() => {
    if (typeof agentTotalItemCount === 'number') return;
    if (loadingMoreItems) return;
    void loadMoreItems({ mode: 'agent' });
  }, [agentTotalItemCount, loadMoreItems, loadingMoreItems]);

  const handleCreateConversation = useCallback(() => {
    setActiveConversation(null, { syncMode: false });
    onCreateConversation?.();
  }, [onCreateConversation, setActiveConversation]);

  const handleSelectConversation = useCallback(
    (conversationId: string) => {
      setActiveConversation(conversationId, { syncMode: false });
      onSelectConversation?.(conversationId);
    },
    [onSelectConversation, setActiveConversation],
  );

  return (
    <Container>
      <Header>
        <Title>{t('agentWidget.conversations')}</Title>
        <CreateBtn type="button" onClick={handleCreateConversation}>
          <Plus size={14} />
          {t('agentWidget.newConversation')}
        </CreateBtn>
      </Header>

      {agentConversations.length === 0 ? (
        <EmptyWrap>
          <EmptyIconWrap>
            <MessageCircle size={28} strokeWidth={1.6} />
          </EmptyIconWrap>
          <EmptyTitle>{t('agentWidget.noConversations')}</EmptyTitle>
          <EmptyDescription>{t('agentWidget.noConversationsDesc')}</EmptyDescription>
        </EmptyWrap>
      ) : (
        <List>
          {teamGroupConversations.length > 0 && (
            <>
              <SectionHeader type="button" onClick={() => setTeamGroupOpen((prev) => !prev)}>
                <SectionTitle>{t('agentWidget.teamGroupChat')}</SectionTitle>
                {teamGroupOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
              </SectionHeader>
              {teamGroupOpen && teamGroupConversations.map((conversation) => {
                const preview = getLastMessagePreview(conversation);
                const teamName = conversation.sharedGroup?.team_name || t('agentWidget.teamSpace');
                return (
                  <ListItem
                    key={conversation.id}
                    type="button"
                    $active={activeId === conversation.id}
                    onClick={() => handleSelectConversation(conversation.id)}
                  >
                    <ItemAvatar>
                      <Users size={14} />
                    </ItemAvatar>
                    <ItemInfo>
                      <ItemTop>
                        <ItemTitle>{conversation.title || conversation.sharedGroup?.title || t('agentWidget.groupChat')}</ItemTitle>
                        <ItemTime>{formatRelTime(conversation.updatedAt)}</ItemTime>
                      </ItemTop>
                      <ItemPreview>{preview}</ItemPreview>
                      <ItemMeta>{teamName}</ItemMeta>
                    </ItemInfo>
                  </ListItem>
                );
              })}
            </>
          )}

          {personalConversations.length > 0 && (
            <>
              <SectionHeader type="button" onClick={() => setPersonalOpen((prev) => !prev)}>
                <SectionTitle>{t('agentWidget.personalChats')}</SectionTitle>
                {personalOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
              </SectionHeader>
              {personalOpen && personalConversations.map((conversation) => {
                const preview = getLastMessagePreview(conversation);
                return (
                  <ListItem
                    key={conversation.id}
                    type="button"
                    $active={activeId === conversation.id}
                    onClick={() => handleSelectConversation(conversation.id)}
                  >
                    <ItemAvatar>
                      {agentAvatar && !agentAvatarError ? (
                        <ItemAvatarImg
                          src={agentAvatar}
                          alt={t('agentWidget.agentAlt')}
                          referrerPolicy="no-referrer"
                          onError={() => setAgentAvatarError(true)}
                        />
                      ) : (
                        'A'
                      )}
                    </ItemAvatar>
                    <ItemInfo>
                      <ItemTop>
                        <ItemTitle>{conversation.title || t('agentWidget.newConversation')}</ItemTitle>
                        <ItemTime>{formatRelTime(conversation.updatedAt)}</ItemTime>
                      </ItemTop>
                      <ItemPreview>{preview}</ItemPreview>
                    </ItemInfo>
                  </ListItem>
                );
              })}
            </>
          )}

          {canLoadMore && (
            <LoadMoreBtn type="button" onClick={() => void loadMoreItems({ mode: 'agent' })} disabled={loadingMoreItems}>
              {loadingMoreItems ? t('agentWidget.loadingMore') : t('agentWidget.loadMore')}
            </LoadMoreBtn>
          )}
        </List>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: transparent;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 4px 8px;
`;

const Title = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  letter-spacing: -0.01em;
`;

const CreateBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: none;
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  padding: 6px 8px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textSecondary};
  background: transparent;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'};
  }
`;

const List = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.dm.listPad};

  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const SectionHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 10px 4px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.01em;

  &:hover {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const SectionTitle = styled.span``;

const ListItem = styled.button<{ $active: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.dm.listItemGap};
  text-align: left;
  border: 1px solid ${({ theme, $active }) =>
    $active
      ? theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'
      : 'transparent'};
  border-radius: ${({ theme }) => theme.dm.listItemRadius};
  padding: ${({ theme }) => theme.dm.listItemPad};
  background: ${({ theme, $active }) =>
    $active
      ? theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#ffffff'
      : 'transparent'};
  box-shadow: ${({ theme, $active }) =>
    $active
      ? theme.mode === 'dark'
        ? '0 1px 4px rgba(0,0,0,0.3), 0 0 0 0.5px rgba(255,255,255,0.06)'
        : '0 1px 4px rgba(0,0,0,0.06), 0 0 0 0.5px rgba(0,0,0,0.04)'
      : 'none'};
  cursor: pointer;
  transition: background 0.15s ease-out, box-shadow 0.15s ease-out;

  &:hover {
    background: ${({ $active, theme }) =>
      $active
        ? theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#ffffff'
        : theme.colors.bgHover};
  }
`;

const ItemAvatar = styled.div`
  flex-shrink: 0;
  width: ${({ theme }) => theme.dm.avatarSize};
  height: ${({ theme }) => theme.dm.avatarSize};
  border-radius: 22%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'linear-gradient(145deg, #434345, #38383a)'
      : 'linear-gradient(145deg, #eaeaed, #e1e1e5)'};
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 1px 4px rgba(0,0,0,0.35)'
      : '0 1px 4px rgba(0,0,0,0.08)'};
  color: ${({ theme }) => (theme.mode === 'dark' ? '#c8c8cc' : '#4a4a4e')};
  font-weight: ${({ theme }) => theme.dm.avatarFontWeight};
  font-size: ${({ theme }) => theme.dm.avatarFontSize};
  user-select: none;
  overflow: hidden;
`;

const ItemAvatarImg = styled.img`
  width: 100%;
  height: 100%;
  border-radius: inherit;
  object-fit: cover;
`;

const ItemInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ItemTop = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ItemTitle = styled.div`
  flex: 1;
  min-width: 0;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.dm.nameFontSize};
  font-weight: ${({ theme }) => theme.dm.nameWeight};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ItemTime = styled.div`
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.6875rem;
`;

const ItemPreview = styled.div`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.dm.previewFontSize};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ItemMeta = styled.div`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.6875rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LoadMoreBtn = styled.button`
  width: 100%;
  border: none;
  border-radius: ${({ theme }) => theme.dm.listItemRadius};
  padding: 8px 10px;
  margin-top: 4px;
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.75rem;
  cursor: pointer;
  transition: background 0.15s ease-out, color 0.15s ease-out;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const EmptyWrap = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 18px;
`;

const EmptyIconWrap = styled.div`
  color: ${({ theme }) => theme.colors.textMuted};
  opacity: 0.52;
  margin-bottom: 10px;
`;

const EmptyTitle = styled.div`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const EmptyDescription = styled.div`
  margin-top: 4px;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

export default AgentWidgetList;
